import type { InMemoryCache } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client';

import type { JobRecommendationFragment } from '@xing-com/crate-jobs-graphql';
import { GetSimilarJobsForUserDocument } from '@xing-com/crate-jobs-graphql';

type UseSimiliarJobsResult = {
  isLoading: boolean;
  isEmpty: boolean;
  hasData: boolean;
  recommendations: JobRecommendationFragment[];
};

export const useSimilarJobs = (): UseSimiliarJobsResult => {
  const { cache } = useApolloClient();

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  (cache as InMemoryCache).policies.addTypePolicies({
    JobRecommendation: {
      fields: {
        // Local-only field with @client directive
        isDeleted(isDeleted = false) {
          return isDeleted;
        },
      },
    },
  });
  const { loading, data } = useQuery(GetSimilarJobsForUserDocument, {
    variables: {
      consumer: 'loggedout.web.startpage.similarjobsrecommendations',
      first: 16,
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const recommendations = (
    data?.viewer?.similarJobsForUserRecommendations?.edges ?? []
  ).reduce<JobRecommendationFragment[]>(
    (prev, { node }) =>
      node.job?.__typename === 'VisibleJob' ? [...prev, node] : prev,
    []
  );

  return {
    isLoading: loading,
    isEmpty: recommendations.length === 0,
    hasData: !!data && !loading,
    recommendations,
  };
};
